import { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  FormErrorMessage,
  Heading,
  Select,
  SimpleGrid,
  Flex,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  apiCreateWorkflow,
  apiUpdateWorkflow,
  apiGetWorkflowLeadSources,
  apiGetWorkflow,
  apiGetUsers,
  apiGetWorkflows,
} from 'api';

import type {
  APIWorkflowLeadSourceData,
  APIWorkflowOverviewData,
  APIUserListResultData,
  APIWorkflowListResultData,
} from 'types/api.types';

import { WORKFLOW_TYPE_OPTIONS } from 'variables/options';

import Card from 'components/card/Card';

import SelectSearch from 'components/fields/SelectSearch';
import MultiSelect from 'components/fields/MultiSelect';

import WorkflowUsers from 'views/new_edit_workflow/components/WorkflowUsers';

const STATUS_OPTIONS = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

const NOTIFY_OFFLINE_USERS_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const WorkflowFormView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    is_active: true,
    lead_source: '',
    owner: null,
    notify_offline_users: true,
    sub_workflows: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    error: workflowError,
    isRefetching: isWorkflowRefetching,
    refetch,
  } = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => apiGetWorkflow(parseInt(id)),
    enabled: isEditMode,
  }) as {
    data: APIWorkflowOverviewData | undefined;
    isLoading: boolean;
    error?: Error;
    refetch: () => Promise<any>;
    isRefetching: boolean;
  };

  // Fetch lead sources for dropdown
  const { data: leadSources = [], isLoading: loadingLeadSources } = useQuery({
    queryKey: ['workflow-lead-sources'],
    queryFn: () => apiGetWorkflowLeadSources(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APIWorkflowLeadSourceData[];
    isLoading: boolean;
    error?: Error;
  };

  // Fetch users for owner dropdown
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => apiGetUsers(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APIUserListResultData[];
    isLoading: boolean;
    error?: Error;
  };

  // Fetch workflows for sub_workflows dropdown
  const { data: workflows = [], isLoading: loadingWorkflows } = useQuery({
    queryKey: ['workflows-all'],
    queryFn: () => apiGetWorkflows(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APIWorkflowListResultData[];
    isLoading: boolean;
    error?: Error;
  };

  // Set form data when workflow data is loaded
  useEffect(() => {
    if (workflowData && isEditMode) {
      setFormData({
        name: workflowData.name || '',
        type: workflowData.type.toString() || '',
        is_active: workflowData.is_active ?? true,
        lead_source: workflowData.lead_source.toString() || '',
        owner: workflowData.owner ? workflowData.owner.toString() : null,
        notify_offline_users: workflowData.notify_offline_users ?? true,
        sub_workflows: workflowData.sub_workflows,
      });
    }
  }, [workflowData, isEditMode]);

  // Create workflow mutation
  const createWorkflowMutation = useMutation({
    mutationFn: (data: any) => apiCreateWorkflow(data),
    onSuccess: (data) => {
      toast({
        title: 'Workflow created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      setErrors({});
      navigate(`/admin/settings/workflows/edit/${data.id}`);
    },
    onError: handleApiError,
  });

  // Update workflow mutation
  const updateWorkflowMutation = useMutation({
    mutationFn: ({ workflowId, data }: { workflowId: number; data: any }) =>
      apiUpdateWorkflow(workflowId, data),
    onSuccess: () => {
      toast({
        title: 'Workflow updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      setErrors({});

      // Invalidate the workflow query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });

      navigate('/admin/settings/workflows');
    },
    onError: handleApiError,
  });

  // Handle API errors
  function handleApiError(error: any) {
    toast({
      title: `Error ${isEditMode ? 'updating' : 'creating'} workflow`,
      description: 'Please check the form for errors',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });

    const formattedErrors: Record<string, string> = {};

    Object.entries(error).forEach(([field, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        formattedErrors[field] = messages[0];
      } else if (typeof messages === 'string') {
        formattedErrors[field] = messages;
      }
    });

    setErrors(formattedErrors);
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === 'is_active' || name === 'notify_offline_users') {
      setFormData((prev) => ({
        ...prev,
        [name]: value === 'true',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubWorkflowsChange = (selectedIds: number[]) => {
    setFormData((prev) => ({
      ...prev,
      sub_workflows: selectedIds || [],
    }));

    if (errors.sub_workflows) {
      setErrors((prev) => ({
        ...prev,
        sub_workflows: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const workflowPayload = {
        name: formData.name,
        type: formData.type,
        is_active: formData.is_active,
        lead_source: formData.lead_source || null,
        owner: formData.owner || null,
        notify_offline_users: formData.notify_offline_users,
        sub_workflows: formData.sub_workflows,
      };

      if (isEditMode && id) {
        updateWorkflowMutation.mutate({
          workflowId: parseInt(id),
          data: workflowPayload,
        });
      } else {
        createWorkflowMutation.mutate(workflowPayload);
      }
    } catch (error) {
      toast({
        title: `Error ${isEditMode ? 'updating' : 'creating'} workflow`,
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  if (
    loadingLeadSources ||
    loadingUsers ||
    loadingWorkflows ||
    (isEditMode && isLoadingWorkflow)
  ) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading data...
        </Flex>
      </Card>
    );
  }

  // Show error state if workflow data failed to load (in edit mode)
  if (isEditMode && workflowError) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading workflow data. Please try again.
        </Flex>
      </Card>
    );
  }

  const isSubmitting =
    createWorkflowMutation.isPending || updateWorkflowMutation.isPending;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card mb="20px">
          <Heading size="md" mb={6} color={textColor}>
            {isEditMode ? 'Edit Workflow' : 'Create New Workflow'}
          </Heading>

          {errors.non_field_errors && (
            <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
              {errors.non_field_errors}
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl isRequired isInvalid={!!errors.name}>
              <FormLabel>Name</FormLabel>
              <Input
                variant="main"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Workflow Name"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.is_active}>
              <FormLabel>Status</FormLabel>
              <Select
                variant="main"
                name="is_active"
                value={formData.is_active.toString()}
                onChange={handleChange}
              >
                {STATUS_OPTIONS.map((statusOption) => (
                  <option
                    key={statusOption.label}
                    value={statusOption.value.toString()}
                  >
                    {statusOption.label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.is_active}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.type}>
              <FormLabel>Type</FormLabel>
              <Select
                variant="main"
                name="type"
                value={formData.type}
                onChange={handleChange}
                placeholder="Select type"
              >
                {WORKFLOW_TYPE_OPTIONS.map((typeOption) => (
                  <option key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.type}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.lead_source}>
              <FormLabel>Lead Source (Landing Page)</FormLabel>
              <SelectSearch
                options={leadSources.map((source) => ({
                  value: source.id.toString(),
                  label: source.name,
                }))}
                value={formData.lead_source}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    lead_source: value.toString(),
                  }))
                }
              />
              <FormErrorMessage>{errors.lead_source}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.owner}>
              <FormLabel>Broker</FormLabel>
              <SelectSearch
                options={users.map((user) => ({
                  value: user.user.id.toString(),
                  label: `${user.user.first_name} ${user.user.last_name} (${user.user.email})`,
                }))}
                value={formData.owner || ''}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    owner: value ? value.toString() : null,
                  }))
                }
                allowClear={true}
                placeholder="No broker"
              />
              <FormErrorMessage>{errors.owner}</FormErrorMessage>
              <Box mt={2} ml="10px" fontSize="sm" color="gray.500">
                Select a broker if this workflow belongs to an individual broker
              </Box>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.notify_offline_users}>
              <FormLabel>Notify Offline Users</FormLabel>
              <Select
                variant="main"
                name="notify_offline_users"
                value={formData.notify_offline_users.toString()}
                onChange={handleChange}
              >
                {NOTIFY_OFFLINE_USERS_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value.toString()}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.notify_offline_users}</FormErrorMessage>
              <Box mt={2} ml="10px" fontSize="sm" color="gray.500">
                Controls if a new lead email is sent to offline users or not
              </Box>
            </FormControl>

            <FormControl isInvalid={!!errors.sub_workflows}>
              <FormLabel>Sub Workflows</FormLabel>
              <MultiSelect
                selectedIds={formData.sub_workflows}
                availableItems={workflows.map((workflow) => ({
                  id: workflow.id,
                  name: workflow.name,
                }))}
                isLoading={loadingWorkflows}
                onChange={handleSubWorkflowsChange}
                label="Select workflow(s)"
                itemLabelSingular="workflow"
                itemLabelPlural="workflows"
              />
              <FormErrorMessage>{errors.sub_workflows}</FormErrorMessage>
              <Box mt={2} ml="10px" fontSize="sm" color="gray.500">
                Select workflows that the new leads should be round robin routed
                into
              </Box>
            </FormControl>
          </SimpleGrid>

          <Flex mt={10} justify="flex-end">
            <Button
              variant="outline"
              mr={3}
              onClick={() => navigate('/admin/settings/workflows')}
              isDisabled={isSubmitting}
            >
              Back to list
            </Button>
            <Button
              variant="brand"
              type="submit"
              isLoading={isSubmitting}
              loadingText={isEditMode ? 'Updating' : 'Creating'}
            >
              {isEditMode ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </Flex>
        </Card>
      </form>
      {workflowData && (
        <WorkflowUsers
          users={workflowData.users}
          handleRefetch={refetch}
          isRefetchingUsers={isWorkflowRefetching}
        />
      )}
    </>
  );
};

export default WorkflowFormView;
